import React, { useState } from 'react';
import { Box, Toolbar } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import { useAuth } from '../../contexts/AuthContext';

// Accessibility: Skip link target
const SKIP_LINK_TARGET = 'main-content';

interface LayoutProps {
  children: React.ReactNode;
}

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 64;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { mustChangePassword } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarCollapse = () => {
    const newVal = !sidebarCollapsed;
    setSidebarCollapsed(newVal);
    localStorage.setItem('sidebar-collapsed', String(newVal));
  };

  const effectiveWidth = sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  // Block navigation to any page other than /profile while force-change is active
  if (mustChangePassword && !location.pathname.startsWith('/profile')) {
    return <Navigate to="/profile?force=1" replace />;
  }

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      {/* Skip Link */}
      <a
        href={`#${SKIP_LINK_TARGET}`}
        className="skip-link"
      >
        ข้ามไปยังเนื้อหาหลัก
      </a>

      <Header
        onMenuClick={handleDrawerToggle}
        onSidebarToggle={handleSidebarCollapse}
        sidebarCollapsed={sidebarCollapsed}
      />

      <Sidebar
        open={mobileOpen}
        onClose={handleDrawerToggle}
        drawerWidth={DRAWER_WIDTH}
        drawerWidthCollapsed={DRAWER_WIDTH_COLLAPSED}
        collapsed={sidebarCollapsed}
      />

      <Box
        component="main"
        id={SKIP_LINK_TARGET}
        role="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          minWidth: 0,
          minHeight: '100vh',
        }}
      >
        <Toolbar />

        <Box
          sx={{
            flex: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
          }}
        >
          {children}
        </Box>

        <Footer />
      </Box>
    </Box>
  );
};

export default Layout;
